import { AfterViewChecked, Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ConfirmationService } from 'primeng/api';
import { forkJoin } from 'rxjs';
import { AuthService } from '@Workspace/auth';
import { eEntityStatus, eReportType, eUserRole } from '@Workspace/_generated/enums';
import type { IReportDto, IReportUserVisitDetailDto, IUserReportsDto } from '@Workspace/_generated/interfaces';
import { PageLoaderService } from '@Workspace/services';
import { AccountController, ReportController } from '@Workspace/_generated/services';

@Component({
    selector: 'reports-page',
    templateUrl: './reports.page.html',
    styleUrls: ['./reports.page.scss']
})
export class ReportsPage implements OnInit, AfterViewChecked, OnDestroy {
    private readonly manageAllSectionKey = 'all-sections';
    private readonly favoritesCategoryKey = 'favorites';
    private readonly emptyGuid = '00000000-0000-0000-0000-000000000000';

    @ViewChild('tableauHost') tableauHost?: ElementRef<HTMLDivElement>;

    searchText = '';
    reportCards: IReportDto[] = [];
    isLoading = false;
    showDefaultViewToast = false;
    isManageMode = false;
    isManageSidebarOpen = false;
    isManageReportMode = false;
    tableauLoadError = '';
    iframeLoadError = '';
    selectedCategoryKey = 'All';
    selectedDefaultCategoryId = 'campaign-type-all';
    selectedDefaultCategoryName = 'All';
    allNavItemCount = 0;
    favoriteReportIds = new Set<string>();
    // Shared sidebar view model used by both grid sidebar and manage sidebar.
    sidebarSections: Array<{
        key: string;
        label: string;
        kind: 'campaign-type' | 'custom';
        isDefault: boolean;
        isLocked: boolean;
        navItems: Array<{
            id: string;
            label: string;
            reportIds: string[];
            reports: IReportDto[];
            isLocked?: boolean;
            displayOrder: number;
        }>;
        visibleNavItems: Array<{
            id: string;
            label: string;
            reportIds: string[];
            reports: IReportDto[];
            isLocked?: boolean;
            displayOrder: number;
        }>;
        displayOrder: number;
    }> = [];
    selectedReport: IReportDto | undefined;
    selectedReportSafeSrc: SafeResourceUrl | null = null;
    selectedManageSectionKey = 'campaign-type';
    // Manage view sections are derived from userReportTreeNodes.
    manageSections: ReportsPage['sidebarSections'] = [];
    defaultManageSections: ReportsPage['sidebarSections'] = [];
    customManageSections: ReportsPage['sidebarSections'] = [];
    manageReportCategoryOptions: Array<{ id: string; label: string }> = [];
    selectedManageNavId = '';
    selectedManageReportId = '';
    manageEmbedType: 'url' | 'code' = 'code';
    isCreatingReport = false;
    isInlineManageReportEdit = false;
    // Single source of truth for create/edit report form values.
    manageReportForm = this.formBuilder.nonNullable.group({
        id: '',
        name: '',
        category: 'FAN365',
        description: '',
        source: '',
        isActive: true,
        visibleRoleIds: new FormControl<number[]>([], { nonNullable: true })
    });
    isAddingSection = false;
    newSectionDraft = '';
    editingSectionKey = '';
    editingSectionDraft = '';
    addingNavForSectionKey = '';
    newNavDraft = '';
    editingNavId = '';
    editingNavDraft = '';
    private draggedManageReportId = '';
    private draggedFromNavId = '';
    // Local mutable hierarchy (sections/navs/reports) used for sidebar rendering and reordering.
    private userReportTreeNodes: IReportDto[] = [];

    readonly roleOptions: { id: number; label: string }[] = [
        { id: eUserRole.Admin as number, label: 'Admin' },
        { id: eUserRole.Associate as number, label: 'Associate' },
        { id: eUserRole.Client_Solution as number, label: 'Client Solution' },
        { id: eUserRole.AdOps as number, label: 'AdOps' },
        { id: eUserRole.LFSocial as number, label: 'LF Social' },
        { id: eUserRole.Media_Activation as number, label: 'Media Activation' },
        { id: eUserRole.Client_Solution as number, label: 'Client Solution' },
        { id: eUserRole.EDGE_Manager as number, label: 'EDGE Manager' },
        { id: eUserRole.AdOps_Manager as number, label: 'AdOps Manager' },
        { id: eUserRole.LFSocial_Manager as number, label: 'LF Social Manager' },
        { id: eUserRole.Media_Activation_Manager as number, label: 'Media Activation Manager' },
    ];

    private lastRenderedTableauSource = '';
    private initialLoadTimer?: ReturnType<typeof setTimeout>;
    private toastTimer?: ReturnType<typeof setTimeout>;

    isUsageTrackingViewActive = false;
    usageTrackingStats: IReportUserVisitDetailDto[] = [];
    usageTrackingSearchText = '';
    expandedUsageReportRows: { [reportId: string]: boolean } = {};
    expandedUsageRoleRows: { [rowKey: string]: boolean } = {};
    usageTrackingSortField: 'reportName' | 'count' | 'lastVisitedOn' = 'lastVisitedOn';
    usageTrackingSortDirection: 'asc' | 'desc' = 'desc';
    isManageOrderLoading = false;
    selectedCategoryId = 'All'
    usageTrackingDateRange: Date[] | null = null;

    private isVisibleToCurrentUser(report: IReportDto): boolean {
        const isAdmin = !!this.authService.userSettings?.permissions?.isAdmin;
        if (isAdmin) { return true; }
        if (!report.isActive) { return false; }
        const userRoleId = Number(this.authService.userSettings?.userRole || 0);
        const roleIds = (report.userRoleIds || '').split(',').map(value => Number((value || '').trim())).filter(value => !Number.isNaN(value) && value > 0);
        return !roleIds.length || (!!userRoleId && roleIds.includes(userRoleId));
    }

    constructor(
        public pageLoaderService: PageLoaderService,
        private sanitizer: DomSanitizer,
        public authService: AuthService,
        private confirmationService: ConfirmationService,
        private userReportController: ReportController,
        private accountController: AccountController,
        private formBuilder: FormBuilder
    ) {}

    // Initializes the page by starting the first load cycle.
    ngOnInit(): void {
        this.selectedDefaultCategoryId = this.denormalizeDefaultReportValue(this.authService.userSettings?.defaultReport || '') || this.selectedDefaultCategoryId;
        this.syncFavoritesFromUserSettings();
        this.startInitialPageLoad();
    }

    // Clears timers and hides the global loader when component is destroyed.
    ngOnDestroy(): void {
        if (this.initialLoadTimer) {
            clearTimeout(this.initialLoadTimer);
            this.initialLoadTimer = undefined;
        }
        if (this.toastTimer) {
            clearTimeout(this.toastTimer);
            this.toastTimer = undefined;
        }
        this.pageLoaderService.hidePageLoader();
    }

    // Triggers Tableau rendering only when a Tableau report is selected.
    ngAfterViewChecked(): void {
        if (this.shouldRenderTableauViz()) {
            this.renderTableauVizFromSource();
        } else {
            this.lastRenderedTableauSource = '';
        }
    }

    // Shows the shared page loader first, then reveals reports content.
    private startInitialPageLoad(): void {
        this.isLoading = true;
        this.pageLoaderService.showPageLoader();
        this.loadUserReports();
    }

    // Loads user reports from the API and builds the report cards and sidebar tree.
    private loadUserReports(): void {
        this.userReportController
            .GetAllReports(0, 2000)
            .subscribe({
                next: result => {
                    const rows = Array.isArray(result?.data) ? result.data : [];
                    this.reportCards = this.buildReportCards(rows);
                    this.userReportTreeNodes = [...rows];
                    this.getSidebarNavItems(rows);
                    this.updateReportCounts();
                    this.applySavedDefaultCategory();

                    this.isLoading = false;
                    this.pageLoaderService.hidePageLoader();
                },
                error: () => {
                    this.reportCards = [];
                    this.sidebarSections = [];
                    this.updateReportCounts();
                    this.isLoading = false;
                    this.pageLoaderService.hidePageLoader();
                }
            });
    }

    // Applies the user's previously saved default category/nav selection once sidebar data is loaded.
    private applySavedDefaultCategory(): void {
        if (!this.selectedDefaultCategoryId || this.selectedDefaultCategoryId === 'campaign-type-all') {
            return;
        }

        if (this.selectedDefaultCategoryId.toLowerCase() === this.favoritesCategoryKey) {
            this.selectCategory(this.favoritesCategoryKey, 'Favorites');
            return;
        }

        for (const section of this.sidebarSections) {
            const navItem = section.navItems.find(nav => nav.id === this.selectedDefaultCategoryId);
            if (navItem) {
                this.selectCategory(navItem.id, navItem.label);
                return;
            }
        }
    }

    // Builds the report card view models from the API data.
    private buildReportCards(rows: IReportDto[]): IReportDto[] {
        return rows
            .filter(row => row.type === eReportType.Report)
            .sort((a, b) => a.displayOrder - b.displayOrder)
            .map(row => ({
                id: row.id,
                reportName: row.reportName,
                categoryName: row.categoryName,
                description: row.description,
                embedType: row.embedType,
                embedCode: row.embedCode,
                isActive: row.isActive,
                userRoleIds: row.userRoleIds,
                displayOrder: row.displayOrder,
                type: row.type,
                parentId: row.parentId,
                entityStatus: row.entityStatus
            }));
    }

    // Recomputes sidebar category counts from currently available cards.
    private updateReportCounts(): void {
        const visibleReports = this.reportCards.filter(report => this.isVisibleToCurrentUser(report));

        this.allNavItemCount = visibleReports.length;
    }

    // Get sidebar sections and nav items based on the current report tree and visible reports.
    private getSidebarNavItems(rows: IReportDto[]): void {
        const isAdmin = !!this.authService.userSettings?.permissions?.isAdmin;
        const visibleReports = this.reportCards.filter(report => this.isVisibleToCurrentUser(report));
        const visibleReportIds = new Set(visibleReports.map(report => report.id));
        const sections = rows
            .filter(x => x.type === eReportType.Section)
            .sort((a, b) => a.displayOrder - b.displayOrder);
        this.sidebarSections = sections
            .map(section => {
                const navItems = rows
                    .filter(x => x.type === eReportType.Nav && x.parentId === section.id)
                    .sort((a, b) => a.displayOrder - b.displayOrder)
                    .map(nav => {
                        const reportIds = rows
                            .filter(x => x.type === eReportType.Report && x.parentId === nav.id && visibleReportIds.has(x.id))
                            .map(x => x.id);

                        return {
                            id: nav.id,
                            label: nav.reportName,
                            reportIds,
                            reports: visibleReports.filter(report => reportIds.includes(report.id)),
                            isLocked: false,
                            displayOrder: nav.displayOrder
                        };
                    });

                const scopedNavItems = isAdmin
                    ? navItems
                    : navItems.filter(nav => nav.reportIds.length > 0);

                const visibleNavItems = section.id === 'campaign-type'
                    ? scopedNavItems.filter(nav => nav.id !== 'campaign-type-all')
                    : scopedNavItems;

                return {
                    key: section.id,
                    label: section.reportName,
                    kind: 'custom' as const,
                    isDefault: false,
                    isLocked: false,
                    navItems: scopedNavItems,
                    visibleNavItems,
                    displayOrder: section.displayOrder
                };
            })
            .filter(x => x.navItems.length);
    }

    // Resolves iframe URL from tableau-viz/iframe markup or a direct URL string.
    private resolveEmbedSource(rawSource: string): string {
        const source = (rawSource || '').trim();
        if (!source) {
            return '';
        }

        if (source.includes('<tableau-viz')) {
            return this.extractMarkupSource(source, 'tableau-viz');
        }

        if (source.includes('<iframe')) {
            return this.extractMarkupSource(source, 'iframe');
        }

        if (source.startsWith('https://') || source.startsWith('http://')) {
            return source;
        }

        if (source.startsWith('www.')) {
            return `https://${source}`;
        }

        return '';
    }

    // Extracts the src attribute for the given markup tag.
    private extractMarkupSource(markup: string, tagName: string): string {
        const regex = new RegExp(`<${tagName}[^>]*\\ssrc=['\"]([^'\"]+)['\"][^>]*>`, 'i');
        const match = markup.match(regex);
        return match && match[1] ? match[1] : '';
    }

    // Returns the list of report cards filtered by the selected category and search term.
    getFilteredReportCards(): IReportDto[] {
        const term = (this.searchText || '').trim().toLowerCase();
        const selectedCategory = (this.selectedCategoryKey || '').trim().toLowerCase();
        const availableCards = this.reportCards.filter(card => this.isVisibleToCurrentUser(card));

        if (term) {
            return availableCards.filter(card => {
                const matchesQuery = (card.reportName || '').toLowerCase().includes(term)
                    || (card.description || '').toLowerCase().includes(term)
                    || (card.categoryName || '').toLowerCase().includes(term);

                return matchesQuery;
            });
        }

        return availableCards.filter(card => {
            const cardNavId = String(card.parentId || '').trim().toLowerCase();
            const categoryMatch = selectedCategory === 'all'
                || (selectedCategory === this.favoritesCategoryKey && this.favoriteReportIds.has(card.id))
                || cardNavId === selectedCategory;
            if (!categoryMatch) {
                return false;
            }

            return true;
        });
    }

    isFavoritesSelected(): boolean {
        return (this.selectedCategoryKey || '').trim().toLowerCase() === this.favoritesCategoryKey;
    }

    getFavoriteCount(): number {
        return this.reportCards
            .filter(card => this.isVisibleToCurrentUser(card) && this.favoriteReportIds.has(card.id))
            .length;
    }

    toggleFavorite(card: IReportDto, event: Event): void {
        event.preventDefault();
        event.stopPropagation();

        if (!card?.id) {
            return;
        }

        if (this.favoriteReportIds.has(card.id)) {
            this.favoriteReportIds.delete(card.id);
        } else {
            this.favoriteReportIds.add(card.id);
        }

        this.saveFavoritesToBackend();
    }

    isFavorite(cardId: string): boolean {
        return !!cardId && this.favoriteReportIds.has(cardId);
    }

    private syncFavoritesFromUserSettings(): void {
        const rawFavoriteReports = String(this.authService.userSettings?.favoriteReports || '').trim();
        this.favoriteReportIds = this.toFavoriteIdSet(rawFavoriteReports);
    }

    private getPreferencesDefaultReportValue(): string {
        return this.normalizeDefaultReportValue(this.selectedDefaultCategoryId);
    }

    private getFavoriteReportsValue(): string {
        return Array.from(this.favoriteReportIds).join(',');
    }

    private saveFavoritesToBackend(): void {
        const defaultReport = this.getPreferencesDefaultReportValue();
        const favoriteReports = this.getFavoriteReportsValue();
        this.accountController.SetUserReportPreferences({ defaultReport, favoriteReports }).subscribe({
            next: async () => {
                this.pageLoaderService.hidePageLoader();
                if (this.authService.userSettings) {
                    this.authService.userSettings.defaultReport = defaultReport;
                    this.authService.userSettings.favoriteReports = favoriteReports;
                    await this.authService.reloadUserSettingsAsync();
                }
            },
            error: () => {
                this.syncFavoritesFromUserSettings();
            }
        });
    }

    private toFavoriteIdSet(value: string): Set<string> {
        if (!value) {
            return new Set<string>();
        }

        return new Set(
            value
                .split(',')
                .map(item => (item || '').trim())
                .filter(item => !!item)
        );
    }

    // Selects the all-sections manage view and resets report editor selection.
    selectManageAll(): void {
        if (this.isReportEditorActive()) {
            return;
        }

        this.selectedManageSectionKey = this.manageAllSectionKey;
        this.updateManageReportCategoryOptions();
        this.selectedManageNavId = '';
        this.selectedManageReportId = '';
        this.isInlineManageReportEdit = false;
        this.loadManageReportDraft(undefined);
    }

    // Opens the manage sidebar and loads the selected report for editing.
    openManageReportForCard(card: IReportDto): void {
        if (!this.isManageMode || !this.authService.userSettings?.permissions?.isAdmin) {
            return;
        }

        this.buildManageSidebarSections();

        const targetSection = this.manageSections.find(section =>
            section.navItems.some(nav => nav.reportIds.includes(card.id))
        ) || this.defaultManageSections[0] || this.customManageSections[0];

        const targetNav = targetSection
            ? targetSection.navItems.find(nav => nav.reportIds.includes(card.id))
                || targetSection.visibleNavItems[0]
                || targetSection.navItems[0]
            : undefined;

        const targetSectionKey = targetSection ? targetSection.key : undefined;
        const targetNavId = targetNav ? targetNav.id : undefined;

        this.isManageReportMode = true;
        this.isCreatingReport = false;
        this.isInlineManageReportEdit = false;
        this.openManageSidebarInternal(targetSectionKey, targetNavId);
        this.selectedManageSectionKey = targetSectionKey || this.selectedManageSectionKey;
        this.selectedManageNavId = targetNavId || this.selectedManageNavId;
        this.selectedManageReportId = card.id;
        this.loadManageReportDraft(card);
        this.updateManageReportCategoryOptions();
    }

    // Opens the manage sidebar and loads the selected report for inline editing.
    openManageReportFromSidebar(sectionKey: string, navId: string, report: IReportDto): void {
        if (!this.authService.userSettings?.permissions?.isAdmin) {
            return;
        }

        this.isManageReportMode = false;
        this.isCreatingReport = false;
        this.isInlineManageReportEdit = true;
        this.selectedManageSectionKey = sectionKey;
        this.selectedManageNavId = navId;
        this.selectedManageReportId = report.id;
        this.loadManageReportDraft(report);
        this.updateManageReportCategoryOptions();
        this.scrollInlineEditorIntoView(report.id);
    }

    // Deletes a report card after confirming with the user.
    deleteReportCard(card: IReportDto): void {
        if (!this.isManageMode || !this.authService.userSettings?.permissions?.isAdmin) {
            return;
        }

        this.requestDeleteReport(card);
    }

    // Deletes a report from the sidebar and selects the next available report.
    deleteReportFromSidebar(sectionKey: string, navId: string, card: IReportDto): void {
        if (!this.authService.userSettings?.permissions?.isAdmin) {
            return;
        }

        this.requestDeleteReport(card, sectionKey, navId);
    }

    // Shared delete entrypoint for grid and sidebar paths.
    private requestDeleteReport(card: IReportDto, sectionKey = '', navId = ''): void {
        this.confirmDeleteReport(card, () => {
            this.deleteReportById(card.id);

            if (!sectionKey || !navId) {
                return;
            }

            const section = this.manageSections.find(item => item.key === sectionKey);
            const nav = section ? section.navItems.find(item => item.id === navId) : undefined;
            const nextReport = nav ? nav.reports[0] : undefined;
            this.selectedManageReportId = nextReport ? nextReport.id : '';
            this.loadManageReportDraft(nextReport);
        });
    }
    // Opens the dialog to add a new report.    
    openAddReportDialog(): void {
        if (!this.isManageMode || !this.authService.userSettings?.permissions?.isAdmin) {
            return;
        }

        this.buildManageSidebarSections();

        const selectedSection = this.manageSections.find(section => section.key === this.selectedManageSectionKey);
        const targetSection = selectedSection || this.defaultManageSections[0] || this.customManageSections[0];
        const targetNav = targetSection
            ? (targetSection.visibleNavItems[0] || targetSection.navItems[0])
            : undefined;

        this.isManageReportMode = true;
        this.isCreatingReport = true;
        this.isInlineManageReportEdit = false;
        this.isManageSidebarOpen = true;

        this.selectedManageSectionKey = targetSection ? targetSection.key : '';
        this.selectedManageNavId = targetNav ? targetNav.id : '';

        this.selectedManageReportId = '';
        this.manageEmbedType = 'code';
        this.manageReportForm.setValue({
            id: '',
            name: '',
            category: this.selectedDefaultCategoryId ?? '',
            description: '',
            source: '',
            isActive: true,
            visibleRoleIds: []
        });
        this.selectedCategoryId = this.selectedDefaultCategoryId ?? '';
        this.updateManageReportCategoryOptions();

    }

    // Determines if the "Save" button in the manage report editor should be disabled.
    isManageReportSaveDisabled(): boolean {
        if (this.isCreatingReport) {
            return !(this.manageReportForm.controls.name.value || '').trim()
                || !(this.manageReportForm.controls.category.value || '').trim()
                || this.manageReportForm.controls.visibleRoleIds.value.length === 0;
        }

        return !(this.manageReportForm.controls.name.value || '').trim();
    }

    // updates the report 
    UpdateReport(): void {
        if (this.isCreatingReport) {
            this.saveNewReport();
            return;
        }

        const report = this.getSelectedManageReportView();
        if (!report) {
            return;
        }

        const formValue = this.manageReportForm.getRawValue();

        report.reportName = (formValue.name || '').trim();
        report.description = (formValue.description || '').trim();
        report.embedCode = (formValue.source || '').trim();
        report.embedType = this.manageEmbedType === 'url' ? 1 : 2;
        report.isActive = !!formValue.isActive;
        report.userRoleIds = (formValue.visibleRoleIds || []).join(',');
        report.parentId = (this.manageReportForm.controls.category.value || '').trim() || report.parentId || this.selectedManageNavId || 'campaign-type-all';
        report.categoryName = this.getNavLabelById(report.parentId);
        const reportNode = this.userReportTreeNodes.find(node => node.type === eReportType.Report && node.id === report.id);
        if (reportNode) {
            reportNode.reportName = report.reportName;
            reportNode.description = report.description;
            reportNode.categoryName = report.categoryName;
            reportNode.embedType = report.embedType;
            reportNode.embedCode = report.embedCode || '';
            reportNode.isActive = report.isActive;
            reportNode.parentId = report.parentId;
        }

        this.executeManageReportCommand(report, () => {
            this.getSidebarNavItems(this.userReportTreeNodes);
            this.buildManageSidebarSections();
            this.updateReportCounts();

            if (this.selectedReport && this.selectedReport.id === report.id) {
                this.openReportDetail(report);
            }

            if (this.isInlineManageReportEdit) {
                this.exitInlineManageReportEdit();
                return;
            }

            this.closeManageSidebar();
        }, undefined, true);
    }

    // Restores editor fields from the current report values.
    cancelManageReportEdit(): void {
        if (this.isInlineManageReportEdit) {
            this.exitInlineManageReportEdit();
            return;
        }

        this.closeManageSidebar();
    }

    // Sets URL/code mode in editor.
    setManageEmbedType(type: 'url' | 'code'): void {
        if (this.manageEmbedType === type) {
            return;
        }

        this.manageEmbedType = type;

        if (type === 'url') {
            const currentSource = this.manageReportForm.controls.source.value || '';
            this.manageReportForm.controls.source.setValue(this.resolveEmbedSource(currentSource) || currentSource);
        }
    }

    // Opens a report in detail view and prepares embed state.
    openReportDetail(card: IReportDto): void {
        this.pageLoaderService.showPageLoader();
        this.selectedReport = card;
        this.tableauLoadError = '';
        this.iframeLoadError = '';
        this.lastRenderedTableauSource = '';
        this.trackReportView(card);

        const rawEmbedSource = (card.embedCode || '').trim();
        if (!rawEmbedSource) {
            this.selectedReportSafeSrc = null;
            this.pageLoaderService.hidePageLoader();
            return;
        }

        const normalizedUrl = this.resolveEmbedSource(rawEmbedSource).trim();
        const hasAllowedProtocol = normalizedUrl.startsWith('https://') || normalizedUrl.startsWith('http://');
        if (!hasAllowedProtocol) {
            this.selectedReportSafeSrc = null;
            this.iframeLoadError = 'Embed URL must start with http:// or https://.';
            this.pageLoaderService.hidePageLoader();
            return;
        }

        this.selectedReportSafeSrc = this.sanitizer.bypassSecurityTrustResourceUrl(normalizedUrl);
    }

    // Records/increments a user's view count for the given report.
    private trackReportView(report: IReportDto): void {
        if (!report?.id) {
            return;
        }

        this.userReportController.TrackUserReport(<IUserReportsDto>{ reportId: report.id }).subscribe({
            error: () => {
            }
        });
    }

    // Returns from detail view to the reports grid.
    backToGrid(): void {
        this.selectedReport = undefined;
        this.tableauLoadError = '';
        this.iframeLoadError = '';
        this.lastRenderedTableauSource = '';
        this.selectedReportSafeSrc = null;
    }

    // Opens the manage sidebar and loads the first editable item.
    openManageSidebar(): void {
        if (!this.authService.userSettings?.permissions?.isAdmin) {
            return;
        }

        this.isManageReportMode = false;
        this.openManageSidebarInternal(this.manageAllSectionKey);
    }

    private openManageSidebarInternal(preferredSectionKey?: string, preferredNavId?: string): void {
        this.buildManageSidebarSections();

        if (preferredSectionKey === this.manageAllSectionKey) {
            this.selectManageAll();
            this.isManageSidebarOpen = true;
            return;
        }

        const sectionExists = !!this.manageSections.find(section => section.key === preferredSectionKey);
        const fallbackSection = this.manageSections.find(section => section.visibleNavItems.length || section.navItems.length)
            || this.manageSections[0];
        this.selectedManageSectionKey = sectionExists
            ? (preferredSectionKey as string)
            : (fallbackSection ? fallbackSection.key : '');
        this.updateManageReportCategoryOptions();

        this.initializeManageEditorForSection();

        if (preferredNavId) {
            const selectedSection = this.getSelectedManageSectionView();
            const preferredNav = selectedSection?.navItems.find(nav => nav.id === preferredNavId);
            if (preferredNav) {
                this.selectedManageNavId = preferredNav.id;
                const firstReport = preferredNav.reports[0];
                this.selectedManageReportId = firstReport ? firstReport.id : '';
                this.loadManageReportDraft(firstReport);
            }
        }

        this.isManageSidebarOpen = true;
    }

    // Closes the manage sidebar modal.
    closeManageSidebar(): void {
        this.isManageSidebarOpen = false;
        this.isManageReportMode = false;
        this.isCreatingReport = false;
        this.isInlineManageReportEdit = false;
    }

    // Locks left-side interactions while report add/edit UI is active.
    isReportEditorActive(): boolean {
        return this.isManageSidebarOpen && (this.isManageReportMode || this.isCreatingReport || this.isInlineManageReportEdit);
    }

    // Changes the active manage section and refreshes the draft.
    selectManageSection(sectionKey: string): void {
        if (this.isReportEditorActive()) {
            return;
        }

        this.selectedManageSectionKey = sectionKey;
        this.updateManageReportCategoryOptions();
        this.isInlineManageReportEdit = false;
        this.initializeManageEditorForSection();
    }

    // Adds a new custom section node and refreshes manage sidebar state.
    confirmAddManageSection(): void {
        const normalized = (this.newSectionDraft || '').trim();
        if (!normalized || this.isManageOrderLoading) {
            return;
        }

        this.isManageOrderLoading = true;

        const newSection: Partial<IReportDto> = {
            reportName: normalized,
            categoryName: '',
            description: '',
            embedType: 2,
            embedCode: '',
            isActive: true,
            displayOrder: 0,
            type: eReportType.Section,
            userRoleIds: '',
            entityStatus: eEntityStatus.Active
        };

        this.executeManageReportCommand(newSection as IReportDto, (createdSection: IReportDto) => {
            const savedSection: IReportDto = {
                ...(newSection as IReportDto),
                ...(createdSection || {}),
                id: (createdSection && createdSection.id) ? createdSection.id : '',
                parentId: (createdSection && createdSection.parentId) ? createdSection.parentId : ''
            };

            this.userReportTreeNodes.push(savedSection);
            this.getSidebarNavItems(this.userReportTreeNodes);
            this.buildManageSidebarSections();
            this.updateReportCounts();
            this.selectedManageSectionKey = savedSection.id;
            this.isAddingSection = false;
            this.newSectionDraft = '';
            this.initializeManageEditorForSection();
            this.isManageOrderLoading = false;
        }, () => {
            this.isManageOrderLoading = false;
        });
    }

    // Starts inline rename mode for a custom section.
    startEditManageSection(sectionKey: string): void {
        const section = this.manageSections.find(item => item.key === sectionKey);
        if (!section || section.isLocked) {
            return;
        }

        this.editingSectionKey = sectionKey;
        this.editingSectionDraft = section.label;
    }

    // Persists a custom section label into the local tree node.
    saveEditManageSection(sectionKey: string): void {
        const section = this.manageSections.find(item => item.key === sectionKey);
        if (!section || section.isLocked) {
            return;
        }

        const normalized = (this.editingSectionDraft || '').trim();
        if (!normalized) {
            return;
        }

        const sectionNode = this.userReportTreeNodes.find(node => node.type === eReportType.Section && node.id === sectionKey);
        if (!sectionNode) {
            return;
        }

        const updatedSection: IReportDto = {
            ...sectionNode,
            reportName: normalized,
            entityStatus: sectionNode.entityStatus || eEntityStatus.Active
        };

        this.executeManageReportCommand(updatedSection, () => {
            sectionNode.reportName = normalized;
            this.getSidebarNavItems(this.userReportTreeNodes);
            this.buildManageSidebarSections();
            this.cancelEditManageSection();
        });
    }

    // Leaves section rename mode and clears draft input.
    cancelEditManageSection(): void {
        this.editingSectionKey = '';
        this.editingSectionDraft = '';
    }

    // Deletes a custom section and all child nav/report nodes.
    deleteManageSection(sectionKey: string): void {
        const section = this.manageSections.find(item => item.key === sectionKey);
        if (!section || section.isDefault || section.isLocked) {
            return;
        }

        const sectionNavIds = this.userReportTreeNodes
            .filter(node => node.type === eReportType.Nav && node.parentId === sectionKey)
            .map(node => node.id);
        const sectionReportCount = this.userReportTreeNodes
            .filter(node => node.type === eReportType.Report && sectionNavIds.includes(node.parentId))
            .length;

        if (sectionReportCount > 0) {
            this.confirmationService.confirm({
                header: 'Cannot Delete',
                key: 'reportsDelete',
                message: 'Reports exist under this section. Please move those reports to a different nav item, then try again.',
                rejectVisible: false,
                acceptLabel: 'Close',
                acceptButtonStyleClass: 'edge-no-button',
                accept: () => {
                    this.confirmationService.close();
                }
            });
            return;
        }

        this.confirmationService.confirm({
            header: 'Confirmation',
            key: 'reportsDelete',
            message: `Delete section "${section.label}"?`,
            rejectVisible: true,
            acceptLabel: 'Yes',
            rejectLabel: 'No',
            acceptButtonStyleClass: 'edge-button',
            rejectButtonStyleClass: 'edge-no-button',
            accept: () => {
                this.executeDeleteManageSection(sectionKey);
                this.confirmationService.close();
            },
            reject: () => {
                this.confirmationService.close();
            }
        });
    }

    private executeDeleteManageSection(sectionKey: string): void {
        const section = this.manageSections.find(item => item.key === sectionKey);
        if (!section || section.isDefault || section.isLocked) {
            return;
        }

        const navIds = this.userReportTreeNodes
            .filter(node => node.type === eReportType.Nav && node.parentId === sectionKey)
            .map(node => node.id);

        const reportIdsToDelete = this.userReportTreeNodes
            .filter(node => node.type === eReportType.Report && navIds.includes(node.parentId))
            .map(node => node.id);

        const deleteNodes = this.userReportTreeNodes.filter(node => {
            if (node.id === sectionKey) {
                return true;
            }
            if (navIds.includes(node.id)) {
                return true;
            }
            return node.type === eReportType.Report && navIds.includes(node.parentId);
        });

        const deleteRequests = deleteNodes.map(node => ({
            ...node,
            entityStatus: eEntityStatus.Deleted
        } as IReportDto));

        this.executeManageReportCommand(deleteRequests, () => {
            this.userReportTreeNodes = this.userReportTreeNodes.filter(node => {
                if (node.id === sectionKey) {
                    return false;
                }
                if (navIds.includes(node.id)) {
                    return false;
                }
                if (node.type === eReportType.Report && navIds.includes(node.parentId)) {
                    return false;
                }
                return true;
            });

            this.reportCards = this.reportCards.filter(report => !reportIdsToDelete.includes(report.id));
            this.getSidebarNavItems(this.userReportTreeNodes);
            this.buildManageSidebarSections();
            this.updateReportCounts();

            if (this.selectedManageSectionKey === sectionKey) {
                const firstCustom = this.customManageSections[0];
                this.selectedManageSectionKey = firstCustom ? firstCustom.key : 'campaign-type';
            }

            this.cancelEditManageSection();
            if (this.addingNavForSectionKey === sectionKey) {
                this.addingNavForSectionKey = '';
                this.newNavDraft = '';
            }
        }, undefined, true);
    }

    // Opens add-nav input for a custom section.
    startAddNavItem(sectionKey: string): void {
        const section = this.manageSections.find(item => item.key === sectionKey);
        if (!section || section.isLocked) {
            return;
        }

        this.addingNavForSectionKey = sectionKey;
        this.newNavDraft = '';
    }

    // Creates a new nav node under the selected custom section.
    confirmAddNavItem(sectionKey: string): void {
        const section = this.manageSections.find(item => item.key === sectionKey);
        const sectionNode = this.userReportTreeNodes.find(node => node.type === eReportType.Section && node.id === sectionKey);
        if (!section || !sectionNode || section.isLocked || this.isManageOrderLoading) {
            return;
        }

        const navLabel = (this.newNavDraft || '').trim();
        if (!navLabel) {
            return;
        }

        this.isManageOrderLoading = true;

        const newNav: Partial<IReportDto> = {
            reportName: navLabel,
            categoryName: '',
            description: '',
            embedType: 2,
            embedCode: '',
            isActive: true,
            displayOrder: 0,
            type: eReportType.Nav,
            parentId: sectionKey,
            userRoleIds: '',
            entityStatus: eEntityStatus.Active
        };

        this.executeManageReportCommand(newNav as IReportDto, (createdNav: IReportDto) => {
            const savedNav: IReportDto = {
                ...(newNav as IReportDto),
                ...(createdNav || {}),
                id: (createdNav && createdNav.id) ? createdNav.id : '',
                parentId: (createdNav && createdNav.parentId) ? createdNav.parentId : sectionKey
            };

            this.userReportTreeNodes.push(savedNav);
            this.getSidebarNavItems(this.userReportTreeNodes);
            this.buildManageSidebarSections();
            this.updateReportCounts();
            this.selectedManageSectionKey = sectionKey;
            this.selectedManageNavId = savedNav.id;
            this.selectedManageReportId = '';
            this.loadManageReportDraft(undefined);

            this.addingNavForSectionKey = '';
            this.newNavDraft = '';
            this.isManageOrderLoading = false;
        }, () => {
            this.isManageOrderLoading = false;
        });
    }

    // Starts inline rename mode for a nav item.
    startEditNavItem(sectionKey: string, navId: string): void {
        const section = this.manageSections.find(item => item.key === sectionKey);
        if (!section || section.isLocked) {
            return;
        }

        const nav = section.navItems.find(item => item.id === navId);
        if (!nav) {
            return;
        }

        this.editingNavId = navId;
        this.editingNavDraft = nav.label;
    }

    // Persists nav label changes into the local nav node.
    saveEditNavItem(sectionKey: string, navId: string): void {
        const section = this.manageSections.find(item => item.key === sectionKey);
        if (!section || section.isLocked) {
            return;
        }

        const nav = section.navItems.find(item => item.id === navId);
        const normalized = (this.editingNavDraft || '').trim();
        if (!nav || !normalized) {
            return;
        }

        const navNode = this.userReportTreeNodes.find(node => node.type === eReportType.Nav && node.id === navId);
        if (!navNode) {
            return;
        }

        const updatedNav: IReportDto = {
            ...navNode,
            reportName: normalized,
            entityStatus: navNode.entityStatus || eEntityStatus.Active
        };

        this.executeManageReportCommand(updatedNav, () => {
            navNode.reportName = normalized;
            this.getSidebarNavItems(this.userReportTreeNodes);
            this.buildManageSidebarSections();
            this.cancelEditNavItem();
        });
    }

    // Leaves nav rename mode and clears draft input.
    cancelEditNavItem(): void {
        this.editingNavId = '';
        this.editingNavDraft = '';
    }

    // Deletes a nav item and all child report nodes from local state.
    deleteNavItem(sectionKey: string, navId: string): void {
        const section = this.manageSections.find(item => item.key === sectionKey);
        if (!section || section.isLocked) {
            return;
        }

        const nav = section.navItems.find(item => item.id === navId);
        const navLabel = nav ? nav.label : 'this nav item';

        const navReportCount = this.userReportTreeNodes
            .filter(node => node.type === eReportType.Report && node.parentId === navId)
            .length;

        if (navReportCount > 0) {
            this.confirmationService.confirm({
                header: 'Cannot Delete',
                key: 'reportsDelete',
                message: 'Reports exist under this nav item. Please move those reports to a different nav item, then try again.',
                rejectVisible: false,
                acceptLabel: 'Close',
                acceptButtonStyleClass: 'edge-no-button',
                accept: () => {
                    this.confirmationService.close();
                }
            });
            return;
        }

        this.confirmationService.confirm({
            header: 'Confirmation',
            key: 'reportsDelete',
            message: `Delete nav item "${navLabel}"?`,
            rejectVisible: true,
            acceptLabel: 'Yes',
            rejectLabel: 'No',
            acceptButtonStyleClass: 'edge-button',
            rejectButtonStyleClass: 'edge-no-button',
            accept: () => {
                this.executeDeleteNavItem(sectionKey, navId);
                this.confirmationService.close();
            },
            reject: () => {
                this.confirmationService.close();
            }
        });
    }

    private executeDeleteNavItem(sectionKey: string, navId: string): void {
        const section = this.manageSections.find(item => item.key === sectionKey);
        if (!section || section.isLocked) {
            return;
        }

        const reportIdsToDelete = this.userReportTreeNodes
            .filter(node => node.type === eReportType.Report && node.parentId === navId)
            .map(node => node.id);

        const deleteNodes = this.userReportTreeNodes.filter(node => {
            if (node.id === navId) {
                return true;
            }
            return node.type === eReportType.Report && node.parentId === navId;
        });

        const deleteRequests = deleteNodes.map(node => ({
            ...node,
            entityStatus: eEntityStatus.Deleted
        } as IReportDto));

        this.executeManageReportCommand(deleteRequests, () => {
            this.userReportTreeNodes = this.userReportTreeNodes.filter(node => {
                if (node.id === navId) {
                    return false;
                }
                if (node.type === eReportType.Report && node.parentId === navId) {
                    return false;
                }
                return true;
            });

            this.reportCards = this.reportCards.filter(report => !reportIdsToDelete.includes(report.id));
            this.getSidebarNavItems(this.userReportTreeNodes);
            this.buildManageSidebarSections();
            this.updateReportCounts();

            if (this.selectedManageNavId === navId) {
                const nextSection = this.getSelectedManageSectionView();
                const nextNav = nextSection ? nextSection.navItems[0] : undefined;
                this.selectedManageNavId = nextNav ? nextNav.id : '';
                const firstReport = nextNav ? nextNav.reports[0] : undefined;
                this.selectedManageReportId = firstReport ? firstReport.id : '';
                this.loadManageReportDraft(firstReport);
            }

            if (this.editingNavId === navId) {
                this.cancelEditNavItem();
            }
        }, undefined, true);
    }

    // Returns currently selected manage section view model.
    getSelectedManageSectionView(): ReportsPage['manageSections'][number] | undefined {
        return this.manageSections.find(section => section.key === this.selectedManageSectionKey);
    }

    // Returns currently selected nav view model inside selected section.
    getSelectedManageNavView(): ReportsPage['manageSections'][number]['navItems'][number] | undefined {
        const section = this.getSelectedManageSectionView();
        return section ? section.navItems.find(nav => nav.id === this.selectedManageNavId) : undefined;
    }

    // Returns currently selected report from flattened report cards list.
    getSelectedManageReportView(): IReportDto | undefined {
        return this.reportCards.find(report => report.id === this.selectedManageReportId);
    }

    // Returns report ids for a specific nav in display order.
    private getNavReportIdsInDisplayOrder(navId: string): string[] {
        return this.userReportTreeNodes
            .filter(node => node.type === eReportType.Report && node.parentId === navId)
            .sort((a, b) => a.displayOrder - b.displayOrder)
            .map(node => node.id)
            .filter(reportId => this.reportCards.some(report => report.id === reportId));
    }

    // Applies per-nav report display order to local tree and cards.
    private applyNavReportDisplayOrder(navId: string, reportIdsInOrder: string[]): void {
        reportIdsInOrder.forEach((reportId, index) => {
            const nextOrder = index + 1;
            const node = this.userReportTreeNodes.find(item => item.type === eReportType.Report && item.id === reportId);
            if (node) {
                node.parentId = navId;
                node.displayOrder = nextOrder;
            }

            const card = this.reportCards.find(item => item.id === reportId);
            if (card) {
                card.parentId = navId;
                card.displayOrder = nextOrder;
            }
        });
    }

    // Saves updated report order for one or more nav groups.
    private persistReportOrderForNavs(navIds: string[]): void {
        const uniqueNavIds = Array.from(new Set((navIds || []).filter(id => !!id)));
        if (!uniqueNavIds.length) {
            this.isManageOrderLoading = false;
            this.buildManageSidebarSections();
            return;
        }

        this.isManageOrderLoading = true;
        const reportPayloads = this.userReportTreeNodes
            .filter(node => node.type === eReportType.Report && uniqueNavIds.includes(node.parentId));

        this.executeManageReportCommand(reportPayloads, () => {
            this.buildManageSidebarSections();
            this.isManageOrderLoading = false;
        }, () => {
            this.isManageOrderLoading = false;
        });
    }

    // Moves a nav item or section up/down and persists display order.
    moveNavItem(sectionKey: string, navId: string, step: -1 | 1): void {
        const isMovingSection = navId === sectionKey;

        if (isMovingSection) {
            const sectionNodes = this.userReportTreeNodes
                .filter(node => node.type === eReportType.Section)
                .sort((a, b) => a.displayOrder - b.displayOrder);

            const fromIndex = sectionNodes.findIndex(node => node.id === sectionKey);
            if (fromIndex < 0) {
                return;
            }

            const toIndex = fromIndex + step;
            if (toIndex < 0 || toIndex >= sectionNodes.length) {
                return;
            }

            const reordered = [...sectionNodes];
            const [moved] = reordered.splice(fromIndex, 1);
            reordered.splice(toIndex, 0, moved);

            reordered.forEach((node, index) => {
                node.displayOrder = index + 1;
            });

            this.isManageOrderLoading = true;
            this.executeManageReportCommand(reordered, () => {
                this.getSidebarNavItems(this.userReportTreeNodes);
                this.buildManageSidebarSections();
                this.isManageOrderLoading = false;
            }, () => {
                this.isManageOrderLoading = false;
            });
            return;
        }

        const navNodes = this.userReportTreeNodes
            .filter(node => node.type === eReportType.Nav && node.parentId === sectionKey)
            .sort((a, b) => a.displayOrder - b.displayOrder);

        const fromIndex = navNodes.findIndex(node => node.id === navId);
        if (fromIndex < 0) {
            return;
        }

        const toIndex = fromIndex + step;
        if (toIndex < 0 || toIndex >= navNodes.length) {
            return;
        }

        const reordered = [...navNodes];
        const [moved] = reordered.splice(fromIndex, 1);
        reordered.splice(toIndex, 0, moved);

        reordered.forEach((node, index) => {
            node.displayOrder = index + 1;
        });

        this.isManageOrderLoading = true;
        this.executeManageReportCommand(reordered, () => {
            this.getSidebarNavItems(this.userReportTreeNodes);
            this.buildManageSidebarSections();
            this.isManageOrderLoading = false;
        }, () => {
            this.isManageOrderLoading = false;
        });
    }

    // Reorders one dragged id before/after a target id inside an id list.
    private reorderIdsAroundTarget(ids: string[], draggedId: string, targetId: string, placeAfter: boolean): string[] {
        const reordered = ids.filter(id => id !== draggedId);
        const targetIndex = reordered.indexOf(targetId);
        const insertIndex = targetIndex < 0 ? reordered.length : (placeAfter ? targetIndex + 1 : targetIndex);
        reordered.splice(insertIndex, 0, draggedId);
        return reordered;
    }

    // Resolves a nav id to display label for badges/form display.
    private getNavLabelById(navId: string): string {
        const normalizedId = (navId || '').trim();
        if (!normalizedId) {
            return '';
        }

        const navNode = this.userReportTreeNodes.find(node => node.type === eReportType.Nav && node.id === normalizedId);
        return navNode ? (navNode.reportName || '') : '';
    }

    // Handles the start of a drag operation for a report in the manage sidebar.
    onManageReportDragStart(event: DragEvent, navId: string, reportId: string): void {
        this.draggedFromNavId = navId;
        this.draggedManageReportId = reportId;

        if (event.dataTransfer) {
            event.dataTransfer.effectAllowed = 'move';
            event.dataTransfer.setData('text/plain', reportId);
        }
    }

    // Handles dropping a dragged report onto another report within the same or different nav item.
    onManageReportDrop(event: DragEvent, targetNavId: string, targetReportId: string): void {
        event.preventDefault();
        event.stopPropagation();
        if (!this.draggedManageReportId || !this.draggedFromNavId) {
            return;
        }

        const section = this.getSelectedManageSectionView();
        if (!section) {
            this.clearManageDragState();
            return;
        }

        const sourceNav = section.navItems.find(nav => nav.id === this.draggedFromNavId);
        const targetNav = section.navItems.find(nav => nav.id === targetNavId);
        if (!sourceNav || !targetNav) {
            this.clearManageDragState();
            return;
        }

        const currentTarget = event.currentTarget as HTMLElement | null;
        const placeAfter = currentTarget
            ? (event.clientY - currentTarget.getBoundingClientRect().top) > (currentTarget.getBoundingClientRect().height / 2)
            : true;

        if (sourceNav.id === targetNav.id) {
            const navReportIds = this.getNavReportIdsInDisplayOrder(sourceNav.id);
            const reorderedSubset = this.reorderIdsAroundTarget(navReportIds, this.draggedManageReportId, targetReportId, placeAfter);
            this.applyNavReportDisplayOrder(sourceNav.id, reorderedSubset);
            this.persistReportOrderForNavs([sourceNav.id]);
            this.clearManageDragState();
            return;
        }

        const sourceReportIds = this.getNavReportIdsInDisplayOrder(sourceNav.id)
            .filter(id => id !== this.draggedManageReportId);
        const targetReportIds = this.getNavReportIdsInDisplayOrder(targetNav.id)
            .filter(id => id !== this.draggedManageReportId);
        const targetIndex = targetReportIds.indexOf(targetReportId);
        const insertIndex = targetIndex < 0 ? targetReportIds.length : (placeAfter ? targetIndex + 1 : targetIndex);
        targetReportIds.splice(insertIndex, 0, this.draggedManageReportId);

        this.applyNavReportDisplayOrder(sourceNav.id, sourceReportIds);
        this.applyNavReportDisplayOrder(targetNav.id, targetReportIds);
        this.persistReportOrderForNavs([sourceNav.id, targetNav.id]);

        this.clearManageDragState();
    }

    // Handles dropping a dragged report onto the end of a nav item, appending it to the list.
    onManageReportDropToNavEnd(event: DragEvent, targetNavId: string): void {
        event.preventDefault();
        event.stopPropagation();
        if (!this.draggedManageReportId || !this.draggedFromNavId) {
            return;
        }

        const section = this.getSelectedManageSectionView();
        if (!section) {
            this.clearManageDragState();
            return;
        }

        const sourceNav = section.navItems.find(nav => nav.id === this.draggedFromNavId);
        const targetNav = section.navItems.find(nav => nav.id === targetNavId);
        if (!sourceNav || !targetNav) {
            this.clearManageDragState();
            return;
        }

        if (sourceNav.id === targetNav.id) {
            const originalNavReportIds = this.getNavReportIdsInDisplayOrder(sourceNav.id);
            const reorderedNavReportIds = originalNavReportIds.filter(id => id !== this.draggedManageReportId);
            reorderedNavReportIds.push(this.draggedManageReportId);

            this.applyNavReportDisplayOrder(sourceNav.id, reorderedNavReportIds);
            this.persistReportOrderForNavs([sourceNav.id]);
            this.clearManageDragState();
            return;
        }

        const sourceReportIds = this.getNavReportIdsInDisplayOrder(sourceNav.id)
            .filter(id => id !== this.draggedManageReportId);
        const targetReportIds = this.getNavReportIdsInDisplayOrder(targetNav.id)
            .filter(id => id !== this.draggedManageReportId);
        targetReportIds.push(this.draggedManageReportId);

        this.applyNavReportDisplayOrder(sourceNav.id, sourceReportIds);
        this.applyNavReportDisplayOrder(targetNav.id, targetReportIds);
        this.persistReportOrderForNavs([sourceNav.id, targetNav.id]);
        this.clearManageDragState();
    }

    // Clears drag metadata after each drag/drop interaction.
    clearManageDragState(): void {
        this.draggedManageReportId = '';
        this.draggedFromNavId = '';
    }

    // Executes a command to manage a report, handling both single and multiple report payloads.
    private executeManageReportCommand(
        payload: IReportDto,
        onSuccess: (response: IReportDto) => void,
        onError?: () => void,
        showLoader?: boolean
    ): void;
    private executeManageReportCommand(
        payload: IReportDto[],
        onSuccess: () => void,
        onError?: () => void,
        showLoader?: boolean
    ): void;
    private executeManageReportCommand(
        payload: IReportDto | IReportDto[],
        onSuccess: ((response: IReportDto) => void) | (() => void),
        onError?: () => void,
        showLoader = false
    ): void {
        if (showLoader) {
            this.pageLoaderService.showPageLoader();
        }

        if (Array.isArray(payload)) {
            if (!payload.length) {
                if (showLoader) {
                    this.pageLoaderService.hidePageLoader();
                }
                return;
            }

            const requests = payload.map(item => this.userReportController.ManageReport(item));
            forkJoin(requests).subscribe({
                next: () => {
                    (onSuccess as () => void)();
                    if (showLoader) {
                        this.pageLoaderService.hidePageLoader();
                    }
                },
                error: () => {
                    if (onError) {
                        onError();
                    }
                    if (showLoader) {
                        this.pageLoaderService.hidePageLoader();
                    }
                }
            });
            return;
        }

        this.userReportController.ManageReport(payload).subscribe({
            next: (response: IReportDto) => {
                (onSuccess as (value: IReportDto) => void)(response);
                if (showLoader) {
                    this.pageLoaderService.hidePageLoader();
                }
            },
            error: () => {
                if (onError) {
                    onError();
                }
                if (showLoader) {
                    this.pageLoaderService.hidePageLoader();
                }
            }
        });
    }

    private deleteReportById(reportId: string): void {
        const report = this.reportCards.find(item => item.id === reportId);
        if (!report) {
            return;
        }

        report.entityStatus = eEntityStatus.Deleted;

        this.executeManageReportCommand(report, () => {
            this.reportCards = this.reportCards.filter(item => item.id !== reportId);

            this.userReportTreeNodes = this.userReportTreeNodes.filter(node => {
                if (node.type !== eReportType.Report) {
                    return true;
                }
                return node.id !== reportId;
            });

            this.getSidebarNavItems(this.userReportTreeNodes);
            this.buildManageSidebarSections();
            this.updateReportCounts();

            if (this.selectedReport && this.selectedReport.id === reportId) {
                this.backToGrid();
            }
        });
    }

    private confirmDeleteReport(card: IReportDto, onAccept: () => void): void {
        this.confirmationService.confirm({
            header: 'Confirmation',
            key: 'reportsDelete',
            message: `Delete report "${card.reportName}"?`,
            rejectVisible: true,
            acceptLabel: 'Yes',
            rejectLabel: 'No',
            acceptButtonStyleClass: 'edge-button',
            rejectButtonStyleClass: 'edge-no-button',
            accept: () => {
                onAccept();
                this.confirmationService.close();
            },
            reject: () => {
                this.confirmationService.close();
            }
        });
    }

    private scrollInlineEditorIntoView(reportId: string): void {
        setTimeout(() => {
            const row = document.getElementById(`manage-report-${reportId}`);
            if (row) {
                row.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
            }
        }, 0);
    }

    private exitInlineManageReportEdit(): void {
        this.isInlineManageReportEdit = false;
        const selectedReport = this.getSelectedManageReportView();
        this.loadManageReportDraft(selectedReport);
    }

    private initializeManageEditorForSection(): void {
        const section = this.getSelectedManageSectionView();
        if (!section || !section.navItems.length) {
            this.selectedManageNavId = '';
            this.selectedManageReportId = '';
            this.loadManageReportDraft(undefined);
            return;
        }

        const sortedNavs = section.visibleNavItems;
        if (!sortedNavs.length) {
            this.selectedManageNavId = '';
            this.selectedManageReportId = '';
            this.loadManageReportDraft(undefined);
            return;
        }

        this.selectedManageNavId = sortedNavs[0].id;
        const firstReport = sortedNavs[0].reports[0];
        this.selectedManageReportId = firstReport ? firstReport.id : '';
        this.loadManageReportDraft(firstReport);
    }

    private loadManageReportDraft(report: IReportDto | undefined): void {
        if (!report) {
            this.manageEmbedType = 'code';
            this.manageReportForm.setValue({
                id: '',
                name: '',
                category: '',
                description: '',
                source: '',
                isActive: false,
                visibleRoleIds: []
            });
            return;
        }

        const source = (report.embedCode || '').trim();
        const normalizedSource = source.trim();
        const isRawUrl = normalizedSource.startsWith('http://')
            || normalizedSource.startsWith('https://')
            || normalizedSource.startsWith('www.');
        const isUrlEmbedType = Number(report.embedType) === 1;
        // Use persisted embedType as primary source of truth. If missing, fall back to source pattern.
        this.manageEmbedType = isUrlEmbedType ? 'url' : (isRawUrl ? 'url' : 'code');
        this.manageReportForm.setValue({
            id: report.id,
            name: report.reportName,
            category: report.parentId || '',
            description: report.description,
            source: this.manageEmbedType === 'url' ? (this.resolveEmbedSource((report.embedCode || '').trim()) || source) : source,
            isActive: report.isActive,
            visibleRoleIds: []
        });
        const visibleRoleIds = (report.userRoleIds || '')
            .split(',')
            .map(value => Number((value || '').trim()))
            .filter(value => !Number.isNaN(value) && value > 0);
        this.manageReportForm.controls.visibleRoleIds.setValue(visibleRoleIds && visibleRoleIds.length ? [...visibleRoleIds] : []);
        this.selectedCategoryId = this.selectedCategoryKey;
    }

    // Save a new report
    private saveNewReport(): void {
        if (!(this.manageReportForm.controls.name.value || '').trim()
            || !(this.manageReportForm.controls.category.value || '').trim()) {
            return;
        }

        const selectedNavId = (this.manageReportForm.controls.category.value || '').trim();
        const formValue = this.manageReportForm.getRawValue();

        const newReport = {
            reportName: (formValue.name || '').trim(),
            categoryName: this.getNavLabelById(selectedNavId),
            description: (formValue.description || '').trim(),
            embedType: this.manageEmbedType === 'url' ? 1 : 2,
            embedCode: formValue.isActive ? (formValue.source || '').trim() : '',
            isActive: !!formValue.isActive,
            userRoleIds: (formValue.visibleRoleIds || []).join(','),
            displayOrder: 1,
            type: eReportType.Report,
            entityStatus: eEntityStatus.Active,
            parentId: selectedNavId
        };

        this.executeManageReportCommand(newReport as IReportDto, (createdReport: IReportDto) => {
            const savedReport: IReportDto = {
                ...newReport,
                ...(createdReport || {}),
                categoryName: (createdReport && createdReport.categoryName)
                    ? createdReport.categoryName
                    : newReport.categoryName,
                id: (createdReport && createdReport.id) ? createdReport.id : '',
                parentId: (createdReport && createdReport.parentId) ? createdReport.parentId : selectedNavId
            };

            this.reportCards = [...this.reportCards, savedReport]
                .sort((a, b) => a.displayOrder - b.displayOrder);

            this.userReportTreeNodes.push({
                id: savedReport.id,
                reportName: savedReport.reportName,
                description: savedReport.description,
                embedType: savedReport.embedType,
                embedCode: savedReport.embedCode || '',
                isActive: savedReport.isActive,
                displayOrder: this.userReportTreeNodes.filter(node => node.type === eReportType.Report).length + 1,
                type: eReportType.Report,
                parentId: savedReport.parentId || selectedNavId,
                userRoleIds: savedReport.userRoleIds || ''
            } as IReportDto);

            this.getSidebarNavItems(this.userReportTreeNodes);
            this.buildManageSidebarSections();
            this.updateReportCounts();
            this.closeManageSidebar();
        }, undefined, true);
    }

    // Indicates whether the selected report should render with tableau-viz.
    shouldRenderTableauViz(): boolean {
        return !!this.selectedReport
            && !!(this.selectedReport.embedCode || '').trim()
            && (this.selectedReport.embedCode || '').toLowerCase().includes('<tableau-viz');
    }

    // Indicates whether the selected report should render in a plain iframe.
    shouldRenderIframe(): boolean {
        return !!this.selectedReport
            && !!(this.selectedReport.embedCode || '').trim()
            && !this.shouldRenderTableauViz();
    }

    // Builds the sidebar sections for the manage sidebar, including built-in and custom sections.
    private buildManageSidebarSections(): void {
        const normalize = (value: string): string => (value || '').trim().toLowerCase();
        const isCampaignType = (id: string, label: string): boolean => {
            return normalize(id) === 'campaign-type' || normalize(label) === 'campaign type';
        };

        const sortedReportCards = [...this.reportCards].sort((a, b) => a.displayOrder - b.displayOrder);
        const reportById = new Map(sortedReportCards.map(report => [report.id, report]));
        const validReportIds = new Set(sortedReportCards.map(report => report.id));

        const sections = this.userReportTreeNodes
            .filter(node => node.type === eReportType.Section)
            .sort((a, b) => {
                const aIsCampaign = isCampaignType(a.id, a.reportName);
                const bIsCampaign = isCampaignType(b.id, b.reportName);
                return aIsCampaign === bIsCampaign
                    ? a.displayOrder - b.displayOrder
                    : (aIsCampaign ? -1 : 1);
            });

        this.manageSections = sections.map(section => {
            const isCampaignTypeSection = isCampaignType(section.id, section.reportName);

            const navItems = this.userReportTreeNodes
                .filter(node => node.type === eReportType.Nav && node.parentId === section.id)
                .sort((a, b) => a.displayOrder - b.displayOrder)
                .map(nav => {
                    const reportIds = (isCampaignTypeSection && nav.id === 'campaign-type-all')
                        ? sortedReportCards.map(report => report.id)
                        : this.userReportTreeNodes
                            .filter(node => node.type === eReportType.Report && node.parentId === nav.id)
                            .sort((a, b) => a.displayOrder - b.displayOrder)
                            .map(node => node.id)
                            .filter(reportId => validReportIds.has(reportId));

                    const reports = reportIds
                        .map(reportId => reportById.get(reportId))
                        .filter((report): report is IReportDto => !!report);

                    return {
                        id: nav.id,
                        label: nav.reportName,
                        reportIds,
                        reports,
                        isLocked: !!(nav as IReportDto & { isLocked?: boolean }).isLocked,
                        displayOrder: nav.displayOrder
                    };
                });

            const visibleNavItems = isCampaignTypeSection
                ? navItems.filter(nav => nav.id !== 'campaign-type-all')
                : navItems;

            return {
                key: section.id,
                label: section.reportName,
                kind: isCampaignTypeSection ? 'campaign-type' : 'custom',
                isDefault: isCampaignTypeSection,
                isLocked: !!(section as IReportDto & { isLocked?: boolean }).isLocked,
                navItems,
                visibleNavItems,
                displayOrder: section.displayOrder
            };
        });

        this.defaultManageSections = this.manageSections.filter(section => section.isDefault);
        this.customManageSections = this.manageSections.filter(section => !section.isDefault);
        this.updateManageReportCategoryOptions();
    }

    // Rebuilds category dropdown options from selected section nav labels.
    private updateManageReportCategoryOptions(): void {
        const normalize = (value: string): string => (value || '').trim().toLowerCase();
        const options: Array<{ id: string; label: string }> = [];

        if (this.manageSections.length) {
            this.manageSections.forEach(section => {
                section.navItems.forEach(nav => {
                    if (nav.id === 'campaign-type-all') {
                        return;
                    }
                    const label = (nav.label || '').trim();
                    if (label) {
                        options.push({ id: nav.id, label });
                    }
                });
            });
        } else {
            this.userReportTreeNodes
                .filter(node => node.type === eReportType.Nav && node.id !== 'campaign-type-all')
                .forEach(node => {
                    const label = (node.reportName || '').trim();
                    if (label) {
                        options.push({ id: node.id, label });
                    }
                });
        }

        const uniqueOptionsMap = new Map<string, { id: string; label: string }>();
        options.forEach(option => {
            const key = normalize(option.id);
            if (key && !uniqueOptionsMap.has(key)) {
                uniqueOptionsMap.set(key, option);
            }
        });
        this.manageReportCategoryOptions = Array.from(uniqueOptionsMap.values());

        if (!this.manageReportCategoryOptions.length) {
            this.manageReportForm.controls.category.setValue('');
            return;
        }

        const currentCategory = normalize(this.manageReportForm.controls.category.value || '');
        const hasCurrent = this.manageReportCategoryOptions
            .some(option => normalize(option.id) === currentCategory);

        if (!hasCurrent) {
            this.manageReportForm.controls.category.setValue(this.manageReportCategoryOptions[0].id);
        }
    }

    // Creates and mounts tableau-viz element from saved embed markup.
    private async renderTableauVizFromSource(): Promise<void> {
        if (!this.tableauHost || !this.selectedReport) {
            return;
        }

        const rawSource = (this.selectedReport.embedCode || '').trim();
        if (!rawSource || this.lastRenderedTableauSource === rawSource) {
            return;
        }

        const parsedDoc = new DOMParser().parseFromString(rawSource, 'text/html');
        const parsedViz = parsedDoc.querySelector('tableau-viz');
        const vizAttributes = parsedViz
            ? Array.from(parsedViz.attributes).reduce((acc, attr) => {
                acc[attr.name] = attr.value || '';
                return acc;
            }, {} as { [key: string]: string })
            : {};

        if (!vizAttributes.src) {
            this.tableauLoadError = 'Missing Tableau src in embed code.';
            return;
        }

        await customElements.whenDefined('tableau-viz');

        this.tableauHost.nativeElement.innerHTML = '';
        this.tableauLoadError = '';

        const vizElement = document.createElement('tableau-viz');

        Object.entries(vizAttributes).forEach(([name, value]) => vizElement.setAttribute(name, value));

        if (!vizElement.hasAttribute('width')) {
            vizElement.setAttribute('width', '100%');
        }
        if (!vizElement.hasAttribute('height')) {
            vizElement.setAttribute('height', '900');
        }
        if (!vizElement.hasAttribute('toolbar')) {
            vizElement.setAttribute('toolbar', 'bottom');
        }
        if (!vizElement.hasAttribute('hide-tabs')) {
            vizElement.setAttribute('hide-tabs', '');
        }
        vizElement.addEventListener('vizloaderror', (event: Event) => {
            const details = (event as CustomEvent).detail as { message?: string; errorCode?: string } | undefined;
            const message = details?.message || details?.errorCode || 'Unable to load Tableau dashboard.';
            this.tableauLoadError = `Tableau load error: ${message}`;
        });

        this.tableauHost.nativeElement.appendChild(vizElement);
        this.lastRenderedTableauSource = rawSource;
    }

    private normalizeDefaultReportValue(value: string): string {
        const normalized = (value || '').trim().toLowerCase();

        if (!normalized || normalized === 'all' || normalized === 'campaign-type-all') {
            return '';
        }

        if (normalized === this.favoritesCategoryKey) {
            return this.emptyGuid;
        }

        return normalized;
    }

    private denormalizeDefaultReportValue(value: string): string {
        const normalized = (value || '').trim().toLowerCase();
        return normalized === this.emptyGuid ? this.favoritesCategoryKey : normalized;
    }

    // Handles sidebar category changes from the Reports page.
    selectCategory(categoryKey: string, categoryName: string): void {
        this.isUsageTrackingViewActive = false;
        this.selectedReport = undefined;
        this.selectedCategoryKey = (categoryKey || '').trim() || 'All';

        if (this.selectedCategoryKey.toLowerCase() === this.favoritesCategoryKey) {
            this.selectedDefaultCategoryId = 'favorites';
            this.selectedDefaultCategoryName = 'Favorites';
            return;
        }

        this.selectedDefaultCategoryId = this.selectedCategoryKey.toLowerCase() === 'all'
            ? 'campaign-type-all'
            : this.selectedCategoryKey;
        this.selectedDefaultCategoryName = (categoryName || '').trim() || 'All';
    }

    // Persists the selected category/nav as the user's default report view, then shows a confirmation toast.
    setSelectedDefaultCategory(): void {
        const defaultReport = this.getPreferencesDefaultReportValue();
        const favoriteReports = this.getFavoriteReportsValue();
        this.accountController.SetUserReportPreferences({ defaultReport, favoriteReports }).subscribe({
            next: async () => {
                if (this.authService.userSettings) {
                    this.authService.userSettings.defaultReport = defaultReport;
                    this.authService.userSettings.favoriteReports = favoriteReports;
                }

                this.selectedDefaultCategoryId = this.denormalizeDefaultReportValue(defaultReport) || 'campaign-type-all';
                await this.authService.reloadUserSettingsAsync();
            }
        }).add(() => {
            this.pageLoaderService.hidePageLoader();
        });

        this.showDefaultViewToast = true;
        if (this.toastTimer) {
            clearTimeout(this.toastTimer);
        }
        this.toastTimer = setTimeout(() => {
            this.showDefaultViewToast = false;
            this.toastTimer = undefined;
        }, 2500);
    }

    isSelectedDefaultView(): boolean {
        const selectedCategory = (this.selectedCategoryKey || '').trim().toLowerCase();
        const savedDefaultValue = this.normalizeDefaultReportValue(String(this.authService.userSettings?.defaultReport || ''));

        if (!selectedCategory) {
            return false;
        }

        const selectedDefaultValue = selectedCategory === 'all'
            ? ''
            : selectedCategory === this.favoritesCategoryKey
                ? 'favorites'
                : this.selectedCategoryKey;

        return this.normalizeDefaultReportValue(selectedDefaultValue) === savedDefaultValue;
    }

    // Opens the Usage Tracking View and loads usage statistics for all reports.
    openUsageTrackingView(): void {
        if (this.isUsageTrackingViewActive) {
            return;
        }

        this.isUsageTrackingViewActive = true;
        this.selectedReport = undefined;
        this.usageTrackingSearchText = '';
        this.usageTrackingDateRange = null;
        this.pageLoaderService.showPageLoader();
        this.userReportController.GetReportUserVisitStats().subscribe({
            next: (stats: IReportUserVisitDetailDto[]) => {
                this.usageTrackingStats = stats || [];
                this.sortUsageTracking(this.usageTrackingSortField, false);
                this.selectedCategoryKey = '';
            },
            error: () => {
                this.usageTrackingStats = [];
                this.pageLoaderService.hidePageLoader();
                this.selectedCategoryKey = '';
            }
        }).add(() => {
            this.pageLoaderService.hidePageLoader();
            this.selectedCategoryKey = '';
        });
    }

    // Sorts the usage tracking statistics based on the specified field and direction.
    sortUsageTracking(field: 'reportName' | 'count' | 'lastVisitedOn', toggleDirection = true): void {
        if (toggleDirection) {
            if (this.usageTrackingSortField === field) {
                this.usageTrackingSortDirection = this.usageTrackingSortDirection === 'asc' ? 'desc' : 'asc';
            } else {
                this.usageTrackingSortField = field;
                this.usageTrackingSortDirection = field === 'reportName' ? 'asc' : 'desc';
            }
        } else {
            this.usageTrackingSortField = field;
        }

        const direction = this.usageTrackingSortDirection === 'asc' ? 1 : -1;
        this.usageTrackingStats = [...(this.usageTrackingStats || [])].sort((a, b) => {
            if (field === 'reportName') {
                return (a.reportName || '').localeCompare(b.reportName || '') * direction;
            }

            if (field === 'count') {
                return ((Number(a.count) || 0) - (Number(b.count) || 0)) * direction;
            }

            const aDate = new Date(a.lastVisitedOn as any).getTime() || 0;
            const bDate = new Date(b.lastVisitedOn as any).getTime() || 0;
            return (aDate - bDate) * direction;
        });
    }

    // Handles messages received from the iframe, such as when the embedded dashboard has loaded.
    @HostListener('window:message', ['$event'])
    onIframeMessage(event: MessageEvent): void {
        setTimeout(() => {
            this.pageLoaderService.hidePageLoader();
        }, 2000);
    }

    // Returns the usage tracking statistics filtered based on the search text.
    getFilteredUsageTrackingStats(): IReportUserVisitDetailDto[] {
        const term = (this.usageTrackingSearchText || '').trim().toLowerCase();
        const dateRange = this.usageTrackingDateRange;

        return (this.usageTrackingStats || []).filter(report => {

            // Search filter
            const matchesSearch =
                !term ||
                (report.reportName || '').toLowerCase().includes(term);

            // Date range filter
            let matchesDateRange = true;

            if (dateRange?.length === 2 && dateRange[0] && dateRange[1]) {
                const startDate = new Date(dateRange[0]);
                const endDate = new Date(dateRange[1]);

                // Include the complete end date
                startDate.setHours(0, 0, 0, 0);
                endDate.setHours(23, 59, 59, 999);

                const lastVisitedDate = new Date(report.lastVisitedOn);

                matchesDateRange =
                    lastVisitedDate >= startDate &&
                    lastVisitedDate <= endDate;
            }

            return matchesSearch && matchesDateRange;
        });
    }
}

